<script setup>
import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import _ from 'lodash'
import IconBase from '@/components/icon/IconBase.vue'
import IconCamera from '../icon/IconCamera.vue'
import IconBin from '../icon/IconBin.vue'
import FormButton from '../Atoms/FormButton.vue'
import PlaylistElements from '../Playlist/PlaylistItems.vue'
import IconFilm from '../icon/IconFilm.vue'

// Store, route, notification
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const router = useRouter()

// Emits
const emit = defineEmits({
	'delete-stream': () => true,
	'toggle-stream': () => true
})

// Props
const props = defineProps({
	stream: {
		type: Object,
		required: true
	},
	cardType: {
		type: String,
		default: 'stream',
		required: false
	},
	height: {
		type: String,
		default: 'auto',
		required: false
	},
	compactMode: {
		type: Boolean,
		default: false,
	},
})


// Refs


// Computed

// Hooks
onMounted(async () => {

})

onUnmounted(() => {

})


// Methods
const onToggleStream = (e) => {
	e.preventDefault()
	e.stopPropagation()
	emit('toggle-stream')
}
const onDeleteStream = (e) => {
	e.preventDefault()
	e.stopPropagation()
	emit('delete-stream')
}

const computedHeight = computed(() => {
	if (props.height === 'sm') {
		return 'h-32'
	}
	if (props.height === 'md') {
		return 'h-32 lg:h-40'
	}
	if (props.height === 'lg') {
		return 'h-32 lg:h-60'
	}
	return 'h-full'
})

const cardRoute = computed(() => {
	if (props.cardType === 'dynamic') return `/dynamic/${props.stream._id}`
	return `/playlists/${props.stream._id}`
})

const isDisabled = computed(() => {
	if (props.cardType === 'dynamic') return !props.stream.enabled
	return props.stream.disabled
})

const computedBorderColor = computed(() => {
	if (isDisabled.value) return 'border-error-light'
	return 'border-success-light'
})

const computedPlayerSourceIcon = computed(() => {
	if (props.stream?.source && !props.stream?.subSource) return IconCamera
	if (props.stream?.subSource && props.stream?.source?.liveParent) return IconFilm
	if (props.stream?.subSource && !props.stream?.source?.liveParent) return IconFilm
	return null
})
</script>

<template>
	<!-- LIST VIEW -->
	<div v-if="compactMode"
		class="flex gap-3 items-center w-full relative rounded-lg group bg-surface-2 transition-colors duration-200 hover:bg-surface-3 px-3 py-2 cursor-pointer"
		@click="router.push(cardRoute)">
		<div class="flex flex-col min-w-0 flex-1">
			<h2 class="truncate text-sm md:text-base text-light-1 font-medium m-0 min-w-0" :title="stream.name">
				{{ stream.name }}
			</h2>
			<section v-if="cardType === 'playlist'" class="min-w-0 truncate">
				<PlaylistElements :items="stream.streams" />
			</section>
			<section v-else-if="cardType === 'dynamic'" class="flex items-center gap-1.5 min-w-0">
				<icon-base class="shrink-0 text-surface-7" v-if="computedPlayerSourceIcon" :width="12" :height="12">
					<component :is="computedPlayerSourceIcon" />
				</icon-base>
				<span v-if="computedPlayerSourceIcon" class="text-tiny text-surface-8 truncate">{{ stream?.source?.name }}</span>
				<span v-else class="text-tiny text-surface-6">No source selected</span>
			</section>
		</div>
		<div class="ml-auto flex items-center gap-2 shrink-0" @click.stop>
			<FormButton isIcon type="danger" size="sm" aria-label="Delete" @click="onDeleteStream">
				<icon-base>
					<icon-bin />
				</icon-base>
			</FormButton>
			<FormButton :type="isDisabled ? 'main' : 'secondary'" size="sm" @click="onToggleStream">
				{{ isDisabled ? 'Enable' : 'Disable' }}
			</FormButton>
		</div>
	</div>

	<!-- GRID VIEW -->
	<router-link v-else class="group hover:no-underline block" :class="computedHeight" :to="cardRoute">
		<article class="bg-surface-2 !rounded-md !p-3 flex flex-col gap-3 !border-t-[6px]"
			:class="[computedHeight, computedBorderColor]">
			<header class="flex items-start justify-between grow">
				<h2 class="text-base font-medium text-surface-text-2 line-clamp-1 lg:line-clamp-2 break-words min-w-0">
					{{ stream.name }}
				</h2>
				<!-- <Badge class="self-start" type="main" pill v-if="isDisabled">
					Disabled
				</Badge> -->
			</header>
			<footer class="flex items-center gap-2 w-full mt-auto">
				<section v-if="cardType === 'playlist'" class="min-w-0 truncate">
					<PlaylistElements :items="stream.streams" />
				</section>
				<section v-else-if="cardType === 'dynamic'" class="flex items-center gap-2 min-w-0">
					<icon-base class="shrink-0 text-surface-9" v-if="computedPlayerSourceIcon">
						<component :is="computedPlayerSourceIcon" />
					</icon-base>
					<span v-if="computedPlayerSourceIcon" class="text-surface-9 text-xs truncate">{{ stream?.source?.name }}</span>
					<span v-else class="text-surface-9 text-xs">No source selected</span>
				</section>
				<div class="flex items-center gap-2 shrink-0 ml-auto">
					<FormButton isIcon type="danger" size="sm" aria-label="Delete" @click="onDeleteStream"
						class="transition-opacity duration-200 lg:opacity-0 group-hover:opacity-100 focus:opacity-100">
						<icon-base>
							<icon-bin />
						</icon-base>
					</FormButton>
					<FormButton :type="isDisabled ? 'main' : 'secondary'" size="sm" @click="onToggleStream">
						{{ isDisabled ? 'Enable' : 'Disable' }}
					</FormButton>
				</div>
			</footer>
		</article>
	</router-link>
</template>

<style scoped></style>
